import type { Geometry } from 'geojson'
import { useEffect, useRef } from 'react'
import MapUnsupported from './map_unsupported'
import useMaplibre, { type HoverIntent, type LayerVisibility, type MapStatus, type PlaceCamera } from './use_maplibre'
import { BASEMAP_NAME, MAP_CONTAINER_ID, PLAIN_BACKGROUND_COLOR } from '../../lib/map_config'
import type { Insets } from '../../lib/camera'
import type { HoverAbout } from '../../lib/hover'
import type { MapReach } from '../../lib/locate'
import type { AtlasLayer, DetailLayer, JournalLayer, MapViewport, WineryLayer } from '../../types/atlas'

interface AtlasMapProps {
  // Null until the deferred country layer arrives; the map does not wait for it.
  worldLayer: AtlasLayer | null
  countryMinZoom: number
  regionLayer: DetailLayer | null
  tastingLayer: JournalLayer
  wishlistLayer: JournalLayer
  wineryLayer: WineryLayer | null
  // Where the winery layer starts drawing dots, for the reader's marks on
  // them. See `UseMaplibreOptions.wineryMarkZoom`.
  wineryMarkZoom?: number
  layerVisibility: LayerVisibility
  selectedSlug: string | null
  selectedWinerySlug: string | null
  // The selected place's own finer outline (`place.outline`), when the page has
  // one. Optional: without it the selection is drawn from the feature the map
  // already holds. See `UseMaplibreOptions.selectedOutline`.
  selectedOutline?: Geometry | null
  camera: PlaceCamera | null
  // What the chrome is covering, asked for whenever the camera moves, so a
  // place lands in the part of the map a reader can see. The layout owns the
  // chrome, so the layout answers. See `UseMaplibreOptions.insets`.
  cameraInsets?: () => Insets
  onStatus: (status: MapStatus) => void
  onZoom: (zoom: number) => void
  onViewport: (viewport: MapViewport) => void
  onHover: (name: string | null, about?: HoverAbout) => void
  // What a click under the pointer would open, when that changes. See
  // `UseMaplibreOptions.onIntent`.
  onIntent?: (intent: HoverIntent | null) => void
  // Where an in-session camera move will land, as it begins. See
  // `UseMaplibreOptions.onDestination`.
  onDestination?: (viewport: MapViewport) => void
  onSelect: (slug: string) => void
  onSelectTasting: (id: number) => void
  onSelectCluster: (slug: string) => void
  onSelectWinery: (slug: string) => void
  // Where the hook leaves the map itself while there is one, for the feature
  // that is fetched after this chunk. See `UseMaplibreOptions.mapHandle`.
  mapHandle?: { current: MapReach | null }
}

/**
 * The map canvas. Imported dynamically by the layout so maplibre-gl — 304 KB
 * gzipped, and code that touches window, document and WebGL at module
 * evaluation — is route-split out of the entry bundle and never evaluated on the
 * server.
 */
export default function AtlasMap({
  worldLayer,
  countryMinZoom,
  regionLayer,
  tastingLayer,
  wishlistLayer,
  wineryLayer,
  wineryMarkZoom,
  layerVisibility,
  selectedSlug,
  selectedWinerySlug,
  selectedOutline = null,
  camera,
  cameraInsets,
  onStatus,
  onZoom,
  onViewport,
  onHover,
  onIntent,
  onDestination,
  onSelect,
  onSelectTasting,
  onSelectCluster,
  onSelectWinery,
  mapHandle,
}: AtlasMapProps) {
  const container = useRef<HTMLDivElement>(null)
  const status = useMaplibre({
    container,
    worldLayer,
    countryMinZoom,
    regionLayer,
    tastingLayer,
    wishlistLayer,
    wineryLayer,
    wineryMarkZoom,
    layerVisibility,
    selectedSlug,
    selectedWinerySlug,
    selectedOutline,
    camera,
    insets: cameraInsets,
    onZoom,
    onViewport,
    onHover,
    onIntent,
    onDestination,
    onSelect,
    onSelectTasting,
    onSelectCluster,
    onSelectWinery,
    mapHandle,
  })

  useEffect(() => {
    onStatus(status)
  }, [onStatus, status])

  return (
    <>
      {/* MapLibre takes the inner node over entirely, so it is hidden from the
          accessibility tree; the layout's atlas list is the readable path. The
          wrapper is focusable and named so the panel has somewhere to return
          focus to on close — the only keyboard path back out of a canvas.

          It is painted the basemap's own paper, inline rather than by a class:
          this is what shows while the canvas is still being built and wherever
          a tile has not arrived, and it has to be the exact value the style's
          background layer draws, which lives in map_config, not in the theme.
          A different grey here is a flash on every load. */}
      <div
        id={MAP_CONTAINER_ID}
        tabIndex={-1}
        role="application"
        aria-label="Atlas map"
        className="absolute inset-0 outline-none"
        style={{ backgroundColor: PLAIN_BACKGROUND_COLOR }}
      >
        {/* Sized with h-full/w-full, NOT absolute inset-0. maplibre-gl.css sets
            `.maplibregl-map { position: relative }` and is imported after
            Tailwind in application.css, so in a rollup build its rule wins the
            source-order tie against `.absolute` and the element becomes
            relative — at which point `inset-0` sizes nothing, the box collapses
            to 0px, and the canvas falls back to its 300px default off-screen.
            The map rendered blank in a production build and not in dev, because
            the dev server injects Tailwind after this file rather than before.
            A percentage height off the already-absolute parent is immune to
            whatever `position` MapLibre gives the node. */}
        <div ref={container} data-testid="atlas-map-canvas" aria-hidden="true" className="h-full w-full" />
      </div>

      {status === 'unsupported' && <MapUnsupported />}

      {/* Below the search bar rather than at the very top, where it sat under
          it on a phone; opaque, like every other surface laid on the map. The
          atlas itself is still drawn: with the basemap gone the style is bare
          paper and the appellations, dots and pins are on it. */}
      {status === 'basemap-error' && (
        <p
          role="status"
          className="absolute inset-x-3 top-28 z-20 mx-auto max-w-xl rounded-2xl bg-amber-50 px-4 py-2.5 text-center text-sm text-amber-950 shadow-chrome ring-1 ring-amber-300 md:inset-x-0 md:top-20"
        >
          The {BASEMAP_NAME} basemap could not be loaded. Region outlines are drawn over a plain background.
        </p>
      )}
    </>
  )
}
